/**
 * mdLite: the Learning Center markdown subset tokenizer (plan
 * docs/plans/lms-plan.md Appendix E "The markdown subset", D10; WP-15).
 *
 * The TypeScript twin of backend/src/services/lms/content/markdown.js: the same
 * grammar, the same token output, the same error rules, ported line for line
 * so the two read side by side. content/__fixtures__/md-cases.json (byte-
 * identical to the backend's copy) holds both to it: __tests__/mdLite.test.tsx
 * runs every case here and the backend's markdown.test.js runs them there. A
 * change here is a change there, in the same commit; the grammar is written
 * out in markdown.js's header.
 *
 * Nothing here produces HTML. MdLite.tsx maps the tokens to React elements, so
 * text is always escaped by React and a link is only ever an http or https URL.
 *
 * The file name: plan 4.8 calls this module content/mdLite.ts, but mdLite.ts
 * beside MdLite.tsx is one module path on a case-insensitive filesystem
 * (Windows, macOS): an import of '../content/MdLite' resolves to the .ts file
 * first, so the component import silently becomes the tokenizer. Hence
 * mdLiteTokenizer.ts; __tests__/mdLite.test.tsx fails on any such pair.
 */
import type { MdBlock, MdBlockResult, MdErrorRule, MdInline, MdInlineResult } from './types';

/** Characters a backslash makes literal (the backend's MD_ESCAPABLE). */
export const MD_ESCAPABLE = '\\`*_[]()#+-.!|><&';

const EM_DASH = '\u2014';
const ATTRIBUTION_PREFIXES = [`${EM_DASH} `, '-- ', 'Source: '];

const LINK_URL_RE = /^https?:\/\/[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?(?::[0-9]{1,5})?(?:[/?#][^\s<>()`]*)?$/i;
const ENTITY_RE = /^&(?:#[0-9]{1,7}|#[xX][0-9a-fA-F]{1,6}|[A-Za-z][A-Za-z0-9]{1,31});/;
const TAG_START_RE = /[A-Za-z/!?]/;
const SPACE_RE = /\s/;

type BlockKind = 'heading' | 'quote' | 'table' | 'ul' | 'ol' | 'para';

class MdError extends Error {
  rule: MdErrorRule;
  line: number;

  constructor(rule: MdErrorRule, message: string, line: number) {
    super(message);
    this.rule = rule;
    this.line = line;
  }
}

function fail(rule: MdErrorRule, message: string, line: number): never {
  throw new MdError(rule, message, line);
}

function isSpace(ch: string | undefined): boolean {
  return ch !== undefined && SPACE_RE.test(ch);
}

function rtrim(s: string): string {
  return s.replace(/\s+$/, '');
}

function mergeText(nodes: MdInline[]): MdInline[] {
  const out: MdInline[] = [];
  for (const node of nodes) {
    const last = out[out.length - 1];
    if (node.type === 'text' && last && last.type === 'text') {
      last.text += node.text;
    } else {
      out.push(node);
    }
  }
  return out;
}

/** The first "**" after `from` that closes bold: content non-empty, not after a space or a backslash. */
function findStrongClose(src: string, from: number): number {
  for (let j = from + 1; j < src.length - 1; j += 1) {
    if (src[j] === '*' && src[j + 1] === '*' && src[j - 1] !== '\\' && !isSpace(src[j - 1])) return j;
  }
  return -1;
}

/** The first "*" after `from` that closes italic: content non-empty, not after a space or a backslash. */
function findEmClose(src: string, from: number): number {
  for (let j = from + 1; j < src.length; j += 1) {
    if (src[j] === '*' && src[j - 1] !== '\\' && !isSpace(src[j - 1])) return j;
  }
  return -1;
}

interface Allow {
  strong: boolean;
  em: boolean;
}

function parseInline(src: string, allow: Allow, line: number): MdInline[] {
  const out: MdInline[] = [];
  let buf = '';
  const flush = () => {
    if (buf !== '') {
      out.push({ type: 'text', text: buf });
      buf = '';
    }
  };
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    const next: string | undefined = src[i + 1];
    if (c === '\\' && next !== undefined && MD_ESCAPABLE.includes(next)) {
      buf += next;
      i += 2;
      continue;
    }
    if (c === '<' && next !== undefined && TAG_START_RE.test(next)) {
      fail('md_html', 'Raw HTML is not allowed (write the text, or escape the "<" with a backslash).', line);
    }
    if (c === '&' && ENTITY_RE.test(src.slice(i))) {
      fail('md_html', 'HTML character references are not allowed (type the character itself).', line);
    }
    if (c === '!' && next === '[') {
      fail('md_image', 'Images by URL are not allowed; use an image slide with an uploaded asset (D10).', line);
    }
    if (c === '`') {
      const end = src.indexOf('`', i + 1);
      if (end < 0) fail('md_code', 'An inline code span opened with a backtick is never closed.', line);
      const code = src.slice(i + 1, end);
      if (code.trim() === '') fail('md_code', 'An inline code span is empty.', line);
      flush();
      out.push({ type: 'code', text: code });
      i = end + 1;
      continue;
    }
    if (c === '[') {
      if (next === '^') fail('md_footnote', 'Footnotes are not allowed; cite the source in the sources list.', line);
      const close = src.indexOf(']', i + 1);
      if (close < 0) {
        buf += c;
        i += 1;
        continue;
      }
      const after = src[close + 1];
      if (after === '(') {
        const end = src.indexOf(')', close + 2);
        if (end < 0) fail('md_link', 'A link opened with "](" is never closed with ")".', line);
        const text = src.slice(i + 1, close);
        const href = src.slice(close + 2, end);
        if (!LINK_URL_RE.test(href)) {
          fail('md_link_url', `Links must point to an http or https address; "${href}" does not.`, line);
        }
        if (text.trim() === '' || /[[\]`*<]/.test(text)) {
          fail('md_link', 'Link text must be plain words (no brackets, backticks, asterisks or tags).', line);
        }
        flush();
        out.push({ type: 'link', href, children: [{ type: 'text', text }] });
        i = end + 1;
        continue;
      }
      if (after === '[') fail('md_reference_link', 'Reference-style links are not allowed; write [text](https://...).', line);
      buf += c;
      i += 1;
      continue;
    }
    if (c === '*') {
      if (next === '*') {
        const start = i + 2;
        if (start >= src.length || isSpace(src[start])) {
          buf += '**';
          i += 2;
          continue;
        }
        if (!allow.strong) fail('md_emphasis', 'Bold cannot be nested inside bold or italic text.', line);
        const close = findStrongClose(src, start);
        if (close < 0) fail('md_emphasis', 'Bold text opened with ** is never closed.', line);
        const children = parseInline(src.slice(start, close), { strong: false, em: true }, line);
        flush();
        out.push({ type: 'strong', children });
        i = close + 2;
        continue;
      }
      const start = i + 1;
      if (start >= src.length || isSpace(src[start])) {
        buf += '*';
        i += 1;
        continue;
      }
      if (!allow.em) fail('md_emphasis', 'Italic cannot be nested inside italic text.', line);
      const close = findEmClose(src, start);
      if (close < 0) fail('md_emphasis', 'Italic text opened with * is never closed.', line);
      const children = parseInline(src.slice(start, close), { strong: false, em: false }, line);
      flush();
      out.push({ type: 'em', children });
      i = close + 1;
      continue;
    }
    buf += c;
    i += 1;
  }
  flush();
  return mergeText(out);
}

function inline(src: string, line: number): MdInline[] {
  return parseInline(src, { strong: true, em: true }, line);
}

/**
 * The block kind a non-blank, right-trimmed line starts, or an MdError for a
 * line the subset refuses wherever it appears.
 */
function classify(line: string, lineNo: number): BlockKind {
  if (/^\s/.test(line)) {
    if (/^\s+(?:[-*+]|[0-9]{1,9}[.)])\s/.test(line)) fail('md_nested_list', 'Lists have one level; nested items are not allowed.', lineNo);
    fail('md_indent', 'A line may not start with spaces or a tab (indented code is not part of the subset).', lineNo);
  }
  if (/^(?:```|~~~)/.test(line)) fail('md_code_fence', 'Code blocks are not allowed; use `inline code`.', lineNo);
  if (/^#{1,6}(?:\s|$)/.test(line)) {
    if (/^### +\S/.test(line)) return 'heading';
    fail('md_heading', 'Only ### headings are allowed (levels 1 and 2 are the section and the slide).', lineNo);
  }
  if (/^(?:[-*_][ \t]*){3,}$/.test(line)) fail('md_hr', 'Horizontal rules are not allowed.', lineNo);
  if (/^=+$/.test(line)) fail('md_heading', 'Underlined headings are not allowed; use ###.', lineNo);
  if (/^\[[^\]]*\]:/.test(line)) fail('md_reference_link', 'Link definitions are not allowed; write [text](https://...).', lineNo);
  if (line[0] === '>') return 'quote';
  if (line[0] === '|') return 'table';
  if (line === '-' || /^[0-9]{1,9}\.$/.test(line)) fail('md_list', 'A list item is empty.', lineNo);
  if (/^- /.test(line)) return 'ul';
  if (/^[*+] /.test(line)) fail('md_list_marker', 'Unordered list items start with "- ".', lineNo);
  if (/^[0-9]{1,9}\. /.test(line)) return 'ol';
  if (/^[0-9]{1,9}\) /.test(line)) fail('md_list_marker', 'Ordered list items start with a number and ". ".', lineNo);
  return 'para';
}

function splitCells(line: string): string[] {
  const inner = line.slice(1, -1);
  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < inner.length; i += 1) {
    const c = inner[i];
    if (c === '\\' && inner[i + 1] === '|') {
      cell += '\\|';
      i += 1;
    } else if (c === '|') {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += c;
    }
  }
  cells.push(cell.trim());
  return cells;
}

interface NumberedText {
  text: string;
  line: number;
}

interface NumberedRow {
  cells: string[];
  line: number;
}

function parseBlocks(lines: string[]): MdBlock[] {
  const blocks: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = rtrim(lines[i]);
    const lineNo = i + 1;
    if (line === '') {
      i += 1;
      continue;
    }
    const kind = classify(line, lineNo);

    if (kind === 'heading') {
      blocks.push({ type: 'heading', level: 3, children: inline(line.replace(/^### +/, '').trim(), lineNo) });
      i += 1;
      continue;
    }

    if (kind === 'quote') {
      const quoted: NumberedText[] = [];
      while (i < lines.length) {
        const q = rtrim(lines[i]);
        if (q === '' || q[0] !== '>') break;
        if (!/^> \S/.test(q) || /^> ?>/.test(q)) {
          fail('md_blockquote', 'A quotation line starts with "> " and text; nested quotations are not allowed.', i + 1);
        }
        quoted.push({ text: q.slice(2), line: i + 1 });
        i += 1;
      }
      const last = quoted[quoted.length - 1];
      const prefix = ATTRIBUTION_PREFIXES.find((p) => last.text.startsWith(p) && last.text.length > p.length);
      if (quoted.length < 2 || !prefix) {
        fail(
          'md_blockquote_attribution',
          `A quotation needs its attribution on its own last line, starting with "${EM_DASH} ", "-- " or "Source: " (6.3).`,
          last.line
        );
      }
      const body = quoted.slice(0, -1);
      blocks.push({
        type: 'blockquote',
        children: inline(body.map((q) => q.text.trim()).join(' '), body[0].line),
        attribution: inline(last.text.slice(prefix.length).trim(), last.line),
      });
      continue;
    }

    if (kind === 'table') {
      const rows: NumberedRow[] = [];
      while (i < lines.length) {
        const r = rtrim(lines[i]);
        if (r === '' || r[0] !== '|') break;
        if (r.length < 2 || r[r.length - 1] !== '|' || r.endsWith('\\|')) {
          fail('md_table', 'Every table row starts and ends with "|".', i + 1);
        }
        rows.push({ cells: splitCells(r), line: i + 1 });
        i += 1;
      }
      if (rows.length < 3) fail('md_table', 'A table needs a header row, a separator row and at least one body row.', rows[0].line);
      const header = rows[0];
      const separator = rows[1];
      if (!separator.cells.every((cell) => /^:?-{3,}:?$/.test(cell))) {
        fail('md_table', 'The second table row must be the separator row, "| --- | --- |".', separator.line);
      }
      const width = header.cells.length;
      if (header.cells.some((cell) => cell === '')) fail('md_table', 'Every header cell needs text.', header.line);
      for (const row of rows.slice(1)) {
        if (row.cells.length !== width) {
          fail('md_table', `Every table row needs ${width} cells, the header's count.`, row.line);
        }
      }
      blocks.push({
        type: 'table',
        header: header.cells.map((cell) => inline(cell, header.line)),
        rows: rows.slice(2).map((row) => row.cells.map((cell) => inline(cell, row.line))),
      });
      continue;
    }

    if (kind === 'ul' || kind === 'ol') {
      const items: MdInline[][] = [];
      const markerRe = kind === 'ul' ? /^- / : /^[0-9]{1,9}\. /;
      while (i < lines.length) {
        const item = rtrim(lines[i]);
        if (item === '') break;
        const itemKind = classify(item, i + 1);
        if (itemKind === 'para') {
          fail('md_list_continuation', 'A list item is one line; put a blank line between a list and the next paragraph.', i + 1);
        }
        if (itemKind !== kind) break;
        items.push(inline(item.replace(markerRe, '').trim(), i + 1));
        i += 1;
      }
      blocks.push({ type: 'list', ordered: kind === 'ol', items });
      continue;
    }

    // Paragraph: this line and every following plain line up to a blank line or another block.
    const parts: string[] = [];
    while (i < lines.length) {
      const p = rtrim(lines[i]);
      if (p === '') break;
      if (parts.length > 0 && classify(p, i + 1) !== 'para') break;
      parts.push(p);
      i += 1;
    }
    blocks.push({ type: 'paragraph', children: inline(parts.join(' '), lineNo) });
  }
  return blocks;
}

/** Tokenize a block markdown field (the *_md fields and reading_md). Stops at the first error. */
export function tokenizeMarkdown(source: unknown): MdBlockResult {
  if (typeof source !== 'string') {
    return { ok: false, blocks: [], errors: [{ rule: 'md_type', line: 1, message: 'Markdown must be a string.' }] };
  }
  try {
    return { ok: true, blocks: parseBlocks(source.split(/\r?\n/)), errors: [] };
  } catch (err) {
    if (err instanceof MdError) {
      return { ok: false, blocks: [], errors: [{ rule: err.rule, line: err.line, message: err.message }] };
    }
    throw err;
  }
}

/** Tokenize an inline field (a bullet, a table cell): inline markup only, one line. */
export function tokenizeInline(text: unknown): MdInlineResult {
  if (typeof text !== 'string') {
    return { ok: false, inlines: [], errors: [{ rule: 'md_type', line: 1, message: 'Inline markdown must be a string.' }] };
  }
  try {
    if (/[\r\n]/.test(text)) fail('md_inline_newline', 'A bullet or table cell is one line; line breaks are not allowed.', 1);
    return { ok: true, inlines: inline(text, 1), errors: [] };
  } catch (err) {
    if (err instanceof MdError) {
      return { ok: false, inlines: [], errors: [{ rule: err.rule, line: err.line, message: err.message }] };
    }
    throw err;
  }
}

/** The visible text of inline tokens (markers and URLs dropped), for aria labels and previews. */
export function inlineText(nodes: MdInline[]): string {
  let out = '';
  for (const node of nodes) {
    if (node.type === 'text' || node.type === 'code') out += node.text;
    else out += inlineText(node.children);
  }
  return out;
}
